import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CompletedActivityPayloadType,
  SyncedExternalPlatforms
} from 'interfaces';
import {
  EMPTY_STRING,
  ACTIVITY_TYPES,
  NUMBER_OF_DAYS
} from 'constants/general';
import {
  fetchUserLevelStats,
  getCompletedActivityDaySummary,
  getEveningActivityStats,
  getMorningActivityStats,
  getUserDetailsCurrentActivityProps,
  getWeeklyFocusBlockSummary,
  updateLongTermGoals,
  updateUserConsent,
  getWeeklyStats,
  getLocalDeviceSettings,
  updateLocalDeviceSettings,
  getUserDetails,
  getLongTermGoals,
  getSyncedExternalPlatforms
} from './extra';
import { initialUserState } from 'store/initial-states';
import { initWeeklySummaryStats } from 'utils/helpers';
import { DEFAULT_LOCAL_DEVICE_SETTING } from 'assets/data';
import {
  FocusMode,
  UserDetailsTypes,
  WeeklyFocusBlockSummary
} from 'interfaces/user.interface';

const user = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    updateWeeklySummaryLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklySummaryUpdating = payload;
    },
    updateMorningStatsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isMorningStatsUpdating = payload;
    },
    updateEveningStatsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isEveningStatsUpdating = payload;
    },
    updateUserDetails: (
      state,
      { payload }: PayloadAction<UserDetailsTypes>
    ) => {
      state.details = payload;
    },
    setFocusedModes: (
      state,
      {
        payload
      }: PayloadAction<{
        focusNames: FocusMode[];
      }>
    ) => {
      state.details.focus_modes = payload?.focusNames ?? [];
    },
    setIsUserAdmin: (state, action) => {
      state.isUserAdmin = action.payload;
    },
    updateIsCurrentActivityPropsUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCurrentActivityPropsUpdating = payload;
    },
    updateLastCompletedActivityLog: (
      state,
      { payload }: PayloadAction<CompletedActivityPayloadType | null>
    ) => {
      state.last_completed_activity_log = payload;
    },
    updateIsMorningRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMorningRoutineCompleted = payload;
    },
    updateIsEveningRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isEveningRoutineCompleted = payload;
    },
    updateIsBreakingRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isBreakingRoutineCompleted = payload;
    },
    updateCompletedRoutines: (state, { payload }: PayloadAction<string[]>) => {
      state.completed_routines = payload;
      if (payload.includes(ACTIVITY_TYPES.MORNING)) {
        state.isMorningRoutineCompleted = true;
      }
      if (payload.includes(ACTIVITY_TYPES.EVENING)) {
        state.isBreakingRoutineCompleted = true;
        state.isEveningRoutineCompleted = true;
      }
    },
    toggleAreOnboardingStatsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areOnboardingStatsLoading = payload;
    },
    toggleAreUserDetailsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areUserDetailsLoading = payload;
    },
    updateMotivationalSummary: (state, { payload }: PayloadAction<string>) => {
      state.motivationalSummary += payload.replaceAll('"', '');
    },
    updateIsMotivationalSummaryLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMotivationalSummaryLoading = payload;
      if (payload) {
        // reset motivational summary for each new generation
        state.motivationalSummary = EMPTY_STRING;
      }
    },
    updateIsMotivationalSummaryStopGenerating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMotivationalSummaryStopGenerating = payload;
    },
    updateIsUpdatingUserPrivacyPolicy: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserPrivacyPolicy = payload;
    },
    updateIsDailySummaryFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isDailySummaryFetching = payload;
    },
    updateIsLogTermGoalUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isLogTermGoalUpdating = payload;
    },
    updateIsFetchingLogTermGoal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingLogTermGoal = payload;
    },
    updateIsGuestUser: (state, { payload }: PayloadAction<boolean>) => {
      state.isGuestUser = payload;
    },
    updateIsWeeklyFocusBlockSummaryUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklyFocusBlockSummaryUpdating = payload;
    },
    updateWeeklyFocusBlockSummary: (
      state,
      { payload }: PayloadAction<WeeklyFocusBlockSummary[]>
    ) => {
      state.weeklyFocusBlockSummary = payload;
    },
    updateIsUsernameUpdating: (state, { payload }: PayloadAction<boolean>) => {
      state.isUsernameUpdating = payload;
    },
    updateUsername: (state, { payload }: PayloadAction<string>) => {
      state.details.username = payload;
    },
    updateIsUserLoggingOut: (state, { payload }: PayloadAction<boolean>) => {
      state.isUserLoggingOut = payload;
    },
    updateIsWeeklyStatsUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklyStatsUpdating = payload;
    },
    updateOpenAIPrompt: (state, { payload }: PayloadAction<string>) => {
      state.openAIPrompt = payload;
    },
    updateIsSavingUserFeedback: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSavingUserFeedback = payload;
    },
    updateShowThankYouModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showThankYouModal = payload;
    },
    updateIsLocalDeviceSettingUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isLocalDeviceSettingUpdating = payload;
    },
    updateSyncedExternalPlatforms: (
      state,
      { payload }: PayloadAction<SyncedExternalPlatforms>
    ) => {
      state.synced_external_platforms = payload;
    },
    updateShowTeamNameModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showTeamNameModal = payload;
    },
    updateIsUpdatingUserProfileDescription: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserProfileDescription = payload;
    },
    updateIsUpdatingUserProfileImage: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserProfileImage = payload;
    },
    updateUserMetadata: (
      state,
      {
        payload
      }: PayloadAction<{
        profile_image: {
          url: string;
          file_path?: string;
        };
        description: string;
      }>
    ) => {
      state.details.metadata = payload;
    },
    updateIsCancelingSubscription: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCancelingSubscription = payload;
    },
    updateShowCancelReasonModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showCancelReasonModal = payload;
    },
    updateIsFetchingSyncedExternalPlatforms: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingSyncedExternalPlatforms = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMorningActivityStats.fulfilled, (state, { payload }) => {
        state.morningCompletionPercent =
          payload?.average_completion_percent ?? 0;
        state.isMorningStatsUpdating = false;
      })
      .addCase(getMorningActivityStats.rejected, (state) => {
        state.isMorningStatsUpdating = false;
      })
      .addCase(getEveningActivityStats.fulfilled, (state, { payload }) => {
        state.eveningCompletionPercent =
          payload?.average_completion_percent ?? 0;
        state.isEveningStatsUpdating = false;
      })
      .addCase(getEveningActivityStats.rejected, (state) => {
        state.isEveningStatsUpdating = false;
      })
      .addCase(
        getUserDetailsCurrentActivityProps.fulfilled,
        (state, { payload }) => {
          state.currentActivityProps = payload;
          state.isCurrentActivityPropsUpdating = false;
        }
      )
      .addCase(getUserDetailsCurrentActivityProps.rejected, (state) => {
        state.isCurrentActivityPropsUpdating = false;
      })
      .addCase(fetchUserLevelStats.fulfilled, (state, { payload }) => {
        state.onboardingStats = { ...payload };
        state.areOnboardingStatsLoading = false;
      })
      .addCase(fetchUserLevelStats.rejected, (state) => {
        state.areOnboardingStatsLoading = false;
      })
      .addCase(updateUserConsent.fulfilled, (state, { payload }) => {
        if (payload) {
          state.details.has_consented_to_terms_of_service = true;
        }
        state.isUpdatingUserPrivacyPolicy = false;
      })
      .addCase(updateLongTermGoals.fulfilled, (state, { payload }) => {
        state.details.long_term_goals = payload ?? [];
        state.isLogTermGoalUpdating = false;
      })
      .addCase(
        getCompletedActivityDaySummary.fulfilled,
        (state, { payload }) => {
          state.dailySummary.focusSummary = payload?.focusSummary ?? [];
          state.dailySummary.daySummaryAVG = payload?.daySummaryAVG ?? [];
          state.dailySummary.daySummarySUM = payload?.daySummarySUM ?? [];
          state.dailySummary.daySummaryDuration =
            payload?.daySummaryDuration ?? [];
          state.isDailySummaryFetching = false;
        }
      )
      .addCase(getWeeklyFocusBlockSummary.fulfilled, (state, { payload }) => {
        state.weeklyFocusBlockSummary = payload;
        state.isWeeklyFocusBlockSummaryUpdating = false;
      })
      .addCase(getWeeklyStats.fulfilled, (state, { payload }) => {
        state.weeklySummaryStats = payload?.length
          ? payload
          : initWeeklySummaryStats(NUMBER_OF_DAYS);
        state.isWeeklyStatsUpdating = false;
      })
      .addCase(getLocalDeviceSettings.fulfilled, (state, { payload }) => {
        state.localDeviceSetting = payload ?? DEFAULT_LOCAL_DEVICE_SETTING;
        state.isLocalDeviceSettingUpdating = false;
      })
      .addCase(updateLocalDeviceSettings.fulfilled, (state, { payload }) => {
        if (payload) {
          state.localDeviceSetting = payload;
          state.details.local_device_settings = payload;
        }
        state.isLocalDeviceSettingUpdating = false;
      })
      .addCase(getUserDetails.fulfilled, (state, { payload }) => {
        state.details = payload;
      })
      .addCase(getLongTermGoals.fulfilled, (state, { payload }) => {
        state.details.long_term_goals = [
          ...state.details.long_term_goals.filter(
            (goal) => !payload.includes(goal)
          ),
          ...payload
        ];
        state.isFetchingLogTermGoal = false;
      })
      .addCase(getLongTermGoals.rejected, (state) => {
        state.isFetchingLogTermGoal = false;
      })
      .addCase(getSyncedExternalPlatforms.fulfilled, (state, { payload }) => {
        state.synced_external_platforms = payload;
        state.isFetchingSyncedExternalPlatforms = false;
      })
      .addCase(getSyncedExternalPlatforms.rejected, (state) => {
        state.isFetchingSyncedExternalPlatforms = false;
      });
  }
});

export const {
  updateUserDetails,
  setFocusedModes,
  updateMorningStatsLoading,
  updateEveningStatsLoading,
  updateWeeklySummaryLoading,
  setIsUserAdmin,
  updateIsCurrentActivityPropsUpdating,
  updateLastCompletedActivityLog,
  updateIsMorningRoutineCompleted,
  updateIsBreakingRoutineCompleted,
  updateIsEveningRoutineCompleted,
  updateCompletedRoutines,
  toggleAreOnboardingStatsLoading,
  toggleAreUserDetailsLoading,
  updateMotivationalSummary,
  updateIsMotivationalSummaryLoading,
  updateIsMotivationalSummaryStopGenerating,
  updateIsUpdatingUserPrivacyPolicy,
  updateIsDailySummaryFetching,
  updateIsLogTermGoalUpdating,
  updateIsGuestUser,
  updateIsWeeklyFocusBlockSummaryUpdating,
  updateWeeklyFocusBlockSummary,
  updateIsUsernameUpdating,
  updateUsername,
  updateIsUserLoggingOut,
  updateIsWeeklyStatsUpdating,
  updateOpenAIPrompt,
  updateIsSavingUserFeedback,
  updateShowThankYouModal,
  updateIsLocalDeviceSettingUpdating,
  updateSyncedExternalPlatforms,
  updateShowTeamNameModal,
  updateIsUpdatingUserProfileImage,
  updateIsUpdatingUserProfileDescription,
  updateUserMetadata,
  updateIsCancelingSubscription,
  updateShowCancelReasonModal,
  updateIsFetchingLogTermGoal,
  updateIsFetchingSyncedExternalPlatforms
} = user.actions;

export default user.reducer;
